// covered-by: frontends/carrie/src/components/__tests__/Markdown.test.tsx
// covered-by: frontends/carrie/src/components/memory-citation/__tests__/MemoryCitationChip.test.tsx

/**
 * The two custom elements `rehype-memory-citation` writes into an answer, drawn.
 *
 * The hast node carries only strings and numbers, so both reach the memory they
 * stand for through `MemoryCitationsContext` by id and scope, as the claim
 * badge reaches its stored citation through `ClaimCitationsContext`.
 */

import { type ReactNode, createContext, useContext } from "react";

import { MemoryCitationChip } from "./MemoryCitationChip";

import { type LinkableMemory } from "../../utils/memoryCitationFields";
import {
  memoryFocusKey,
  useMemoryFocus,
} from "../../utils/memoryCitationFocus";

/** The memories the body being rendered can cite. */
export const MemoryCitationsContext = createContext<readonly LinkableMemory[]>(
  [],
);

/** The chip, after the sentence a memory supports. */
export function MemoryCitationTag({
  memoryId,
  scope,
}: {
  memoryId?: string;
  scope?: string;
}) {
  const memories = useContext(MemoryCitationsContext);
  const memory = memories.find(
    (m) => m.id === memoryId && m.scope === (scope ?? ""),
  );
  // why null and not the children: a chip for a memory the context no longer holds would draw a number that names nothing.
  return memory ? <MemoryCitationChip memory={memory} /> : null;
}

/**
 * One run of the sentence a memory supports.
 *
 * A sentence is several of these when it holds bold text or a claim link, since
 * each text node is wrapped on its own; they all share the memory's key, so they
 * tint together.
 */
export function MemorySupportTag({
  memoryId,
  scope,
  part,
  children,
}: {
  memoryId?: string;
  scope?: string;
  part?: number;
  children?: ReactNode;
}) {
  const key = memoryFocusKey(scope ?? "", memoryId ?? "");
  const { lit } = useMemoryFocus(key);
  return (
    <span
      data-testid={`memory-citation-sentence-${scope ?? ""}-${memoryId ?? ""}-${part ?? 0}`}
      data-lit={lit ? "true" : "false"}
      className="puntt-memory-support"
    >
      {children}
    </span>
  );
}
